import { Dispatch, SetStateAction } from 'react';
import { UseFormHandleSubmit } from 'react-hook-form';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Icon } from 'components/ui/icon';
import NumericKeypad from './numpad';
import PaymentInfoForm from './payment-info';
import { IVirtualTerminalForm } from './payment-collection';
import { VIRTUAL_TERMINAL } from 'state/virtualTerminal/type';

interface IProps {
  paymentData: IVirtualTerminalForm;
  setPaymentData: Dispatch<SetStateAction<IVirtualTerminalForm>>;
  originalAmount: number;
  setOriginalAmount: Dispatch<SetStateAction<number>>;
  isPersonalInfoFormOpen: boolean;
  setIsPersonalInfoFormOpen: Dispatch<SetStateAction<boolean>>;
  submitToSDK: boolean;
  setSubmitToSDK: Dispatch<SetStateAction<boolean>>;
  isUnpaidTicketFlow: boolean;
  handleSubmit: UseFormHandleSubmit<IVirtualTerminalForm>;
  onSubmit: () => void;
  onSubmitToSDK: (data: IVirtualTerminalForm) => void;
  locations: VIRTUAL_TERMINAL.ILocation[];
  locationSelected?: VIRTUAL_TERMINAL.ILocation;
  drawers: VIRTUAL_TERMINAL.IDrawer[];
  drawerSelected?: VIRTUAL_TERMINAL.IDrawer;
  isSellTreezEntity: boolean;
  onLocationSelect: (data: VIRTUAL_TERMINAL.ILocation) => void;
  onDrawerSelect: (data: VIRTUAL_TERMINAL.IDrawer) => void;
}

const CheckoutTab = ({
  paymentData,
  setPaymentData,
  originalAmount,
  setOriginalAmount,
  isPersonalInfoFormOpen,
  setIsPersonalInfoFormOpen,
  submitToSDK,
  setSubmitToSDK,
  isUnpaidTicketFlow,
  handleSubmit,
  onSubmit,
  onSubmitToSDK,
  locations,
  locationSelected,
  drawers,
  drawerSelected,
  isSellTreezEntity,
  onLocationSelect,
  onDrawerSelect,
}: IProps): JSX.Element => {
  const hasPaymentInfo = Boolean(
    paymentData.phone || paymentData.firstName || paymentData.email
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full sm:max-w-md md:max-w-lg mx-auto sm:mx-0 mb-4"
    >
      {!isPersonalInfoFormOpen && (
        <Card className="border-0 shadow-none">
          <div className="border border-gray-blue-20 rounded-3xl mt-1">
            <button
              type="button"
              data-testid="payment_details"
              onClick={() => {
                setSubmitToSDK(false);
                setIsPersonalInfoFormOpen(true);
              }}
              className="block w-full rounded-t-3xl rounded-b-none border-0 bg-transparent text-left p-5 sm:p-6 cursor-pointer text-base sm:text-sm"
            >
              <div className="flex justify-between items-center w-full">
                <div className="flex items-center shrink-0 mr-3">
                  <span className="bg-gray-blue-5 rounded-lg p-2 flex items-center justify-center text-muted-foreground">
                    <Icon name="id-badge" size={20} aria-hidden="true" />
                  </span>
                </div>
                <div className="flex flex-col items-start grow">
                  {!hasPaymentInfo && <span>Enter payment info</span>}
                  <span className="flex text-sm font-medium">
                    {paymentData.firstName && (
                      <span
                        className="flex justify-start items-center truncate"
                        style={{ maxWidth: 320 }}
                      >
                        <Icon
                          name="user"
                          size={16}
                          className="mr-1"
                          aria-hidden="true"
                        />
                        <span className="truncate">
                          {paymentData.firstName}{' '}
                          {paymentData.lastName && paymentData.lastName}
                        </span>
                      </span>
                    )}
                  </span>
                  {hasPaymentInfo && (
                    <ul className="flex flex-col items-start mb-0 pl-1 text-sm text-gray-blue-70 max-w-xs sm:w-80 w-full list-none">
                      {[
                        paymentData.phone,
                        paymentData.email,
                        paymentData.ticketAlphaNumId,
                      ]
                        .filter(Boolean)
                        .map((item, index) => (
                          <li
                            key={index}
                            className="flex items-center before:content-['•'] before:mr-2 before:text-current"
                          >
                            <span className="inline-block overflow-hidden whitespace-nowrap text-ellipsis max-w-[50vw] sm:max-w-62">
                              {item}
                            </span>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
                <div className="flex items-center justify-center shrink-0 ml-3">
                  <Icon
                    name={hasPaymentInfo ? 'edit' : 'chevron-right'}
                    size={20}
                    className="text-muted-foreground"
                    aria-hidden="true"
                  />
                </div>
              </div>
            </button>
            <div className="w-11/12 border-b border-gray-blue-20 mx-auto" />
            <div className="px-4 pb-4 w-full">
              <NumericKeypad
                amount={originalAmount}
                setAmount={setOriginalAmount}
                isUnpaidTicketFlow={isUnpaidTicketFlow}
              />
              <div className="px-4">
                <Button
                  type="submit"
                  size="full"
                  className="h-13 text-xl font-medium rounded-xl shadow-sm hover:enabled:-translate-y-px active:enabled:scale-[0.99] transition-all duration-150 justify-end"
                  disabled={originalAmount === 0}
                  iconEnd="arrow-right"
                  iconSize={20}
                  iconClassName="text-dark-green-text"
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
      {isPersonalInfoFormOpen && (
        <PaymentInfoForm
          isDrawerOpen={isPersonalInfoFormOpen}
          submitToSDK={submitToSDK}
          setIsDrawerOpen={setIsPersonalInfoFormOpen}
          onSubmit={(data: IVirtualTerminalForm) => {
            if (submitToSDK) {
              onSubmitToSDK({ ...paymentData, ...data });
            } else {
              setPaymentData({
                ...paymentData,
                ...data,
              });
            }
          }}
          paymentData={paymentData}
          isUnpaidTicketFlow={isUnpaidTicketFlow}
          locations={locations}
          locationSelected={locationSelected}
          drawers={drawers}
          drawerSelected={drawerSelected}
          isSellTreezEntity={isSellTreezEntity}
          onLocationSelect={onLocationSelect}
          onDrawerSelect={onDrawerSelect}
        />
      )}
    </form>
  );
};

export default CheckoutTab;
